import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  type Contrast,
  contrasts,
  defaultContrast,
  defaultCorners,
  defaultPalette,
  type Palette,
  palettes,
} from '../styles/axes'
import { persistStorage } from './storage'

export const APPEARANCE_STORAGE_KEY = 'chromaframe-appearance'

/**
 * Keys adopted (in this order) when the current key is absent. `index.html`'s
 * pre-paint script reads the same keys; the contract test keeps them in step.
 */
export const APPEARANCE_LEGACY_KEYS = [
  'aframe-appearance',
  'chromaframe-theme',
  'aframe-theme',
]

export type ThemeId = 'light' | 'dark'

export function isDarkTheme(id: ThemeId): boolean {
  return id !== 'light'
}

/** Legacy (pre-redesign) theme ids that read as light, for migration. */
/** Retired theme ids that map to light; index.html's pre-paint script mirrors this list. */
export const LEGACY_LIGHT_THEMES = [
  'tokyonight-day',
  'hc-light',
  'chroma-prism',
  'chroma-frost',
  'chroma-bloom',
  'hhkb-light',
  'hhkb-sky-light',
  'hhkb-wisteria-light',
  'hhkb-dandelion-light',
  'hhkb-wasabi-light',
  'hhkb-sakura-light',
]
const LEGACY_LIGHT = new Set(LEGACY_LIGHT_THEMES)

function normalizeThemeId(id: string | undefined): ThemeId {
  return id === 'light' || (id !== undefined && LEGACY_LIGHT.has(id))
    ? 'light'
    : 'dark'
}

/**
 * Read + retire the retired standalone theme store's key. The theme store
 * lived under `chromaframe-theme` (with its own `aframe-theme` legacy); when
 * theme folded into this store we adopt its value once, then drop the key.
 */
function adoptThemeId(): ThemeId {
  for (const key of ['chromaframe-theme', 'aframe-theme']) {
    const raw = localStorage.getItem(key)
    if (raw === null) continue
    localStorage.removeItem(key)
    try {
      const parsed = JSON.parse(raw) as { state?: { themeId?: string } }
      return normalizeThemeId(parsed.state?.themeId)
    } catch {
      // corrupted payload — keep scanning
    }
  }
  return 'dark'
}

export type TextSizeId = 'sm' | 'md' | 'lg'

export interface TextSize {
  id: TextSizeId
  label: string
}

export const TEXT_SIZES: TextSize[] = [
  { id: 'sm', label: 'Small' },
  { id: 'md', label: 'Default' },
  { id: 'lg', label: 'Large' },
]

export const PALETTES = palettes

/** Pre-v4 palette ids and the current palette each one becomes. */
export const LEGACY_PALETTES: Record<string, Palette> = {
  purple: 'violet',
  blue: 'indigo',
  electric: 'indigo',
  teal: 'iris',
  ember: 'iris',
}

/** A current id unchanged, a legacy id mapped, anything else the default. */
export function normalizePalette(id: string | undefined): Palette {
  if (id !== undefined) {
    if ((palettes as readonly string[]).includes(id)) return id as Palette
    if (Object.hasOwn(LEGACY_PALETTES, id)) return LEGACY_PALETTES[id]
  }
  return defaultPalette
}

interface AppearanceSelection {
  textSizeId: TextSizeId
  paletteId: Palette
}

export function applyAppearanceAttributes(
  element: Pick<HTMLElement, 'setAttribute'>,
  selection: AppearanceSelection & { themeId: ThemeId },
) {
  element.setAttribute('data-theme', selection.themeId)
  element.setAttribute('data-text-size', selection.textSizeId)
  element.setAttribute('data-palette', selection.paletteId)
  element.setAttribute('data-corners', defaultCorners)
}

interface AppearanceStore {
  themeId: ThemeId
  textSizeId: TextSizeId
  paletteId: Palette
  setTheme: (id: ThemeId) => void
  setTextSize: (id: TextSizeId) => void
  setPalette: (id: Palette) => void
}

export const useAppearanceStore = create<AppearanceStore>()(
  persist(
    (set) => ({
      themeId: 'dark',
      textSizeId: 'md',
      paletteId: defaultPalette,
      setTheme: (themeId) => set({ themeId }),
      setTextSize: (textSizeId) => set({ textSizeId }),
      setPalette: (paletteId) => set({ paletteId }),
    }),
    {
      name: APPEARANCE_STORAGE_KEY,
      // v3 dropped the corners preference (corner style is always square);
      // v4 moved the palettes to the chroma ids (see LEGACY_PALETTES).
      version: 4,
      // The theme key is adopted as a fallback too, so a user who only ever
      // toggled theme (no appearance payload) still rehydrates it here.
      storage: persistStorage(APPEARANCE_STORAGE_KEY, APPEARANCE_LEGACY_KEYS),
      // v1 held only text/palette/corners: theme lived in the standalone
      // store. Adopt its key once, then persist theme here from now on.
      migrate: (persisted): AppearanceSelection & { themeId: ThemeId } => {
        const s = persisted as Partial<AppearanceStore>
        return {
          themeId:
            s.themeId != null ? normalizeThemeId(s.themeId) : adoptThemeId(),
          textSizeId: s.textSizeId ?? 'md',
          paletteId: normalizePalette(s.paletteId),
        }
      },
      partialize: (s) => ({
        themeId: s.themeId,
        textSizeId: s.textSizeId,
        paletteId: s.paletteId,
      }),
    },
  ),
)

/**
 * The contrast axis has its own persisted key (appearance-axes: a new axis gets
 * its own attribute and its own key). The pre-paint script in `index.html`
 * reads the same key and takes the same fallback.
 */
export const CONTRAST_STORAGE_KEY = 'chromaframe-contrast'

/** The OS preference, taken when nothing is stored. */
export function preferredContrast(): Contrast {
  return typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-contrast: more)').matches
    ? 'more'
    : defaultContrast
}

function normalizeContrast(id: unknown): Contrast | undefined {
  return typeof id === 'string' && (contrasts as readonly string[]).includes(id)
    ? (id as Contrast)
    : undefined
}

interface ContrastStore {
  contrastId: Contrast
  setContrast: (id: Contrast) => void
}

export const useContrastStore = create<ContrastStore>()(
  persist(
    (set) => ({
      contrastId: preferredContrast(),
      setContrast: (contrastId) => set({ contrastId }),
    }),
    {
      name: CONTRAST_STORAGE_KEY,
      version: 1,
      storage: persistStorage(CONTRAST_STORAGE_KEY),
      // The key only exists once a person has chosen; until then, and for a
      // value the axis no longer has, the OS preference decides.
      merge: (persisted, current) => ({
        ...current,
        contrastId:
          normalizeContrast(
            (persisted as Partial<ContrastStore> | undefined)?.contrastId,
          ) ?? preferredContrast(),
      }),
      partialize: (s) => ({ contrastId: s.contrastId }),
    },
  ),
)
